import type { BaseSyntheticEvent, Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { LEAD_DETAILS_FORMS, NOTE_CHANGE_TYPE } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import type { ValueOf } from 'cpool-utils';
import { objectKeys } from 'cpool-utils';
import type { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getEmployment } from '../components/Overview/CustomerData/General/utils/getEmployment';
import { prepareWohnsituation } from '../components/Overview/CustomerData/General/utils/prepareWohnsituation';
import useLead from '../hooks/useLead';
import type { Schema_BeschaeftigungForm } from '~/features/leadDetails/schemas/employment.schema.ts';
import type { SchemaExpensesForm } from '~/features/leadDetails/schemas/expenses.schema.ts';
import type { SchemaIncomeForm } from '~/features/leadDetails/schemas/income.schema.ts';
import type { SchemaLivingSituationForm } from '~/features/leadDetails/schemas/living.schema.ts';
import type { SchemaPersonal } from '~/features/leadDetails/schemas/personal.schema.ts';
import type { SchemaRealEstatesForm } from '~/features/leadDetails/schemas/realEstates.schema.ts';
import { getHousingFlags } from '~/features/leadDetails/utils/getHousingFlags.ts';
import { showSubmitToast } from '~/features/leadDetails/utils/toasts.tsx';
import { toExpensesFormValues } from '~/features/leadDetails/utils/toExpensesFormValues.ts';
import { toIncomeFormValues } from '~/features/leadDetails/utils/toIncomeFormValues.ts';
import { setChangeType } from '~/store/queryChangeTypeStore';
import { setQueryPosition } from '~/store/queryPositionStore';
import { appendPositionToQueryKey } from '~/utils/appendPositionToQueryKey';
import { createRequiredContext } from '~/utils/createRequiredContext';
import { queryKeys } from '~/utils/queryKeys';

const formCommands = {
    addChild: 'addChild',
    addExpenseObligationToSupport: 'addExpenseObligationToSupport',
    addExpenseOther: 'addExpenseOther',
    addExpensePrivateHealthInsurance: 'addExpensePrivateHealthInsurance',
    addExpenseRental: 'addExpenseRental',
    addIncomeCapital: 'addIncomeCapital',
    addIncomeOther: 'addIncomeOther',
    addIncomePension: 'addIncomePension',
    addIncomeSecondaryEmployment: 'addIncomeSecondaryEmployment',
    addIncomeSpouse: 'addIncomeSpouse',
    addRealEstate: 'addRealEstate',
    changeApplicantAddress: 'changeApplicantAddress',
    changeApplicantEmail: 'changeApplicantEmail',
    changeApplicantLivingSituation: 'changeApplicantLivingSituation',
    changeApplicantMaritalStatus: 'changeApplicantMaritalStatus',
    changeApplicantName: 'changeApplicantName',
    changeApplicantPhone: 'changeApplicantPhone',
    changeApplicantPreviousAddress: 'changeApplicantPreviousAddress',
    changeBank: 'changeBank',
    changeCar: 'changeCar',
    changeChild: 'changeChild',
    changeExpenseObligationToSupport: 'changeExpenseObligationToSupport',
    changeExpenseOther: 'changeExpenseOther',
    changeExpensePrivateHealthInsurance: 'changeExpensePrivateHealthInsurance',
    changeExpenseRental: 'changeExpenseRental',
    changeIncomeCapital: 'changeIncomeCapital',
    changeIncomeOther: 'changeIncomeOther',
    changeIncomePension: 'changeIncomePension',
    changeIncomeSecondaryEmployment: 'changeIncomeSecondaryEmployment',
    changeIncomeSpouse: 'changeIncomeSpouse',
    changeRealEstate: 'changeRealEstate',
    deleteApplicantNotes: 'deleteApplicantNotes',
    removeChild: 'removeChild',
    removeExpenseObligationToSupport: 'removeExpenseObligationToSupport',
    removeExpenseOther: 'removeExpenseOther',
    removeExpensePrivateHealthInsurance: 'removeExpensePrivateHealthInsurance',
    removeExpenseRental: 'removeExpenseRental',
    removeIncomeCapital: 'removeIncomeCapital',
    removeIncomeOther: 'removeIncomeOther',
    removeIncomePension: 'removeIncomePension',
    removeIncomeSecondaryEmployment: 'removeIncomeSecondaryEmployment',
    removeIncomeSpouse: 'removeIncomeSpouse',
    removeRealEstate: 'removeRealEstate',
    updateApplicantBirthData: 'updateApplicantBirthData',
    updateApplicantResidenceData: 'updateApplicantResidenceData',
};

type DirtyState = { dirty: boolean; dirtyFields: Record<string, unknown> };

const isDirtyState = (dirtyState: DirtyState) => {
    const hasDirtyFields =
        dirtyState.dirtyFields &&
        typeof dirtyState.dirtyFields === 'object' &&
        Object.keys(dirtyState.dirtyFields).length > 0;
    return Boolean(hasDirtyFields && dirtyState.dirty);
};

type Status = {
    error?: unknown;
    saved: boolean;
    changes: boolean;
    form: string;
    commands?: string[];
    position?: { forms?: number; notes?: number };
    breakSubmit?: string[];
};
export type SetSubmitStatus = (status: {
    error?: unknown;
    saved: boolean;
    changes: boolean;
    commands?: string[];
    position?: { forms?: number; notes?: number };
    breakSubmit?: string[];
}) => void;

type ValidateForm = () => Promise<boolean>;

type SubmitHandler = (
    setSubmitStatus: SetSubmitStatus,
) => (
    e?: BaseSyntheticEvent<React.FormEvent<HTMLFormElement>, HTMLFormElement, HTMLFormElement> | undefined,
) => Promise<void>;

type ResetMap = {
    [LEAD_DETAILS_FORMS.PERSONAL_DATA]: UseFormReset<SchemaPersonal> | null;
    [LEAD_DETAILS_FORMS.REAL_ESTATE]: UseFormReset<SchemaRealEstatesForm> | null;
    [LEAD_DETAILS_FORMS.EXPENSES]: UseFormReset<SchemaExpensesForm> | null;
    [LEAD_DETAILS_FORMS.INCOME]: UseFormReset<SchemaIncomeForm> | null;
    [LEAD_DETAILS_FORMS.LIVING_SITUATION]: UseFormReset<NonNullable<SchemaLivingSituationForm>> | null;
    [LEAD_DETAILS_FORMS.EMPLOYMENT_APPLICANT1]: UseFormReset<{
        beschaeftigung: Schema_BeschaeftigungForm;
    }> | null;
    [LEAD_DETAILS_FORMS.EMPLOYMENT_APPLICANT2]: UseFormReset<{
        beschaeftigung: Schema_BeschaeftigungForm;
    }> | null;
};

export type LeadDetailsFormContextType = {
    /**
     * Ids of the income and expense rows the user added since the page was opened, in the order the user
     * added them. The stored data is grouped by type and carries no order of its own, so the rows can only
     * keep their position for as long as the page stays open.
     */
    addedRowIds: string[];
    registerAddedRow: (id: string) => void;
    registerSubmit: (handleSubmit: SubmitHandler, formId: string, validate?: ValidateForm) => void;
    registerDirtyState: (formId: string, { dirty, dirtyFields }: DirtyState) => void;
    registerReset: <T extends keyof ResetMap>(formId: T, reset: ResetMap[T]) => void;
    trigger: () => Promise<boolean>;
    isDirty: () => boolean;
    /**
     * Reactive counterpart to `isDirty()`: usable for rendering (e.g. enabling
     * the save / discard buttons) because it triggers a re-render on change.
     */
    hasDirtyForms: boolean;
    reset: () => void;
    resetAll: (data: SchemaLoanRequestResolver_Get) => void;
    hasSelfOccupiedRealEstateApplicant1: boolean;
    hasSelfOccupiedRealEstateApplicant2: boolean;
    hasSelfOccupiedRealEstateShared: boolean;
    isLivingInOwnHouseApplicant1: boolean;
    isLivingInOwnHouseApplicant2: boolean;
    isLivingInSharedHousehold: boolean;
    setHasSelfOccupiedRealEstateApplicant1: Dispatch<SetStateAction<boolean>>;
    setHasSelfOccupiedRealEstateApplicant2: Dispatch<SetStateAction<boolean>>;
    setHasSelfOccupiedRealEstateShared: Dispatch<SetStateAction<boolean>>;
    setIsLivingInOwnHouseApplicant1: Dispatch<SetStateAction<boolean>>;
    setIsLivingInOwnHouseApplicant2: Dispatch<SetStateAction<boolean>>;
    setIsLivingInSharedHousehold: Dispatch<SetStateAction<boolean>>;
};

const [useLeadDetailsFormContext, ContextProvider] =
    createRequiredContext<LeadDetailsFormContextType>('LeadDetailsFormContext');

type Props = {
    children: React.ReactNode;
    leadId?: string;
};

const LeadDetailsFormContextProvider: React.FC<Props> = ({ children, leadId }) => {
    const queryClient = useQueryClient();
    const { data: lead } = useLead(leadId || '');
    const { t } = useTranslation();
    const ref = useRef<Record<string, SubmitHandler>>({});
    const validators = useRef<Record<string, ValidateForm>>({});
    const dirtyStates = useRef<Record<string, DirtyState>>({});
    const resetFunctions = useRef<ResetMap>({
        EMPLOYMENT_APPLICANT1: null,
        EMPLOYMENT_APPLICANT2: null,
        EXPENSES: null,
        INCOME: null,
        LIVING_SITUATION: null,
        PERSONAL_DATA: null,
        REAL_ESTATE: null,
    });
    const [hasSelfOccupiedRealEstateApplicant1, setHasSelfOccupiedRealEstateApplicant1] = useState(false);
    const [hasSelfOccupiedRealEstateApplicant2, setHasSelfOccupiedRealEstateApplicant2] = useState(false);
    const [hasSelfOccupiedRealEstateShared, setHasSelfOccupiedRealEstateShared] = useState(false);
    const [isLivingInOwnHouseApplicant1, setIsLivingInOwnHouseApplicant1] = useState(false);
    const [isLivingInOwnHouseApplicant2, setIsLivingInOwnHouseApplicant2] = useState(false);
    const [isLivingInSharedHousehold, setIsLivingInSharedHousehold] = useState(false);

    const [dirtyFormIds, setDirtyFormIds] = useState<string[]>([]);
    const [addedRowIds, setAddedRowIds] = useState<string[]>([]);

    const registerAddedRow = useCallback((id: string) => setAddedRowIds((ids) => [...ids, id]), []);

    const clearDirtyState = useCallback(() => {
        dirtyStates.current = {};
        setDirtyFormIds([]);
    }, []);

    const registerDirtyState = useCallback(
        (formId: string, dirtyState: DirtyState) => {
            dirtyStates.current[formId] = dirtyState;
            const isFormDirty = isDirtyState(dirtyState);
            setDirtyFormIds((previous) => {
                const isRegistered = previous.includes(formId);
                if (isFormDirty && !isRegistered) return [...previous, formId];
                if (!isFormDirty && isRegistered) return previous.filter((id) => id !== formId);
                return previous;
            });
        },
        [dirtyStates],
    );

    const registerSubmit = useCallback(
        (handleSubmit: SubmitHandler, formId: string, validate?: ValidateForm) => {
            ref.current[formId] = handleSubmit;
            if (validate) validators.current[formId] = validate;
        },
        [ref],
    );

    const registerReset = useCallback(
        <T extends keyof ResetMap>(formId: T, reset: ResetMap[T]) => {
            resetFunctions.current[formId] = reset;
        },
        [resetFunctions],
    );

    const resetAll = useCallback(
        (data: SchemaLoanRequestResolver_Get) => {
            objectKeys(resetFunctions.current).forEach((formId) => {
                if (!resetFunctions.current[formId]) return;
                switch (formId) {
                    case LEAD_DETAILS_FORMS.PERSONAL_DATA:
                        // NOTE: we "cast" the data here because in the Form-Component we did the same
                        resetFunctions.current[formId](data as unknown as SchemaPersonal);
                        break;
                    case LEAD_DETAILS_FORMS.EMPLOYMENT_APPLICANT1:
                        resetFunctions.current[formId]({ beschaeftigung: getEmployment(data, false).employmentData });
                        break;
                    case LEAD_DETAILS_FORMS.EMPLOYMENT_APPLICANT2:
                        resetFunctions.current[formId]({ beschaeftigung: getEmployment(data, true).employmentData });
                        break;
                    case LEAD_DETAILS_FORMS.EXPENSES:
                        resetFunctions.current[formId](toExpensesFormValues(data.haushalt.ausgaben));
                        break;
                    case LEAD_DETAILS_FORMS.INCOME:
                        resetFunctions.current[formId](toIncomeFormValues(data.haushalt.einnahmen));
                        break;
                    case LEAD_DETAILS_FORMS.LIVING_SITUATION:
                        resetFunctions.current[formId]({
                            antragsteller1: {
                                familienstand: data.antragsteller1.personendaten.familienstand,
                                wohnsituation: prepareWohnsituation(data.antragsteller1.wohnsituation),
                            },
                            antragsteller2: {
                                familienstand: data.antragsteller2?.personendaten.familienstand,
                                wohnsituation: data.antragsteller2
                                    ? prepareWohnsituation(data.antragsteller2.wohnsituation)
                                    : undefined,
                            },
                            kinder: data.haushalt.kinder,
                        });
                        break;
                    case LEAD_DETAILS_FORMS.REAL_ESTATE:
                        resetFunctions.current[formId]({ immobilien: data.haushalt.immobilien });
                        break;
                    default:
                        break;
                }
            });
            const housingFlags = getHousingFlags(data);
            setHasSelfOccupiedRealEstateApplicant1(housingFlags.hasSelfOccupiedRealEstateApplicant1);
            setHasSelfOccupiedRealEstateApplicant2(housingFlags.hasSelfOccupiedRealEstateApplicant2);
            setHasSelfOccupiedRealEstateShared(housingFlags.hasSelfOccupiedRealEstateShared);
            setIsLivingInOwnHouseApplicant1(housingFlags.isLivingInOwnHouseApplicant1);
            setIsLivingInOwnHouseApplicant2(housingFlags.isLivingInOwnHouseApplicant2);
            setIsLivingInSharedHousehold(housingFlags.isLivingInSharedHousehold);
            // A reset can unmount the forms in the same commit (for example while
            // discarding before navigation), so effects cannot reliably report clean state.
            clearDirtyState();
        },
        [clearDirtyState],
    );

    const reset = useCallback(() => {
        if (lead) {
            resetAll(lead);
            return;
        }
        clearDirtyState();
    }, [clearDirtyState, lead, resetAll]);

    const isDirty = useCallback(
        () => Object.values(dirtyStates.current).find((dirtyState) => isDirtyState(dirtyState)) !== undefined,
        [],
    );

    const prevLeadRef = useRef(lead);
    useEffect(() => {
        if (prevLeadRef.current && lead && lead !== prevLeadRef.current && !isDirty()) {
            resetAll(lead);
        }
        prevLeadRef.current = lead;
    });

    const handleStatuses = useCallback((statuses: Status[]) => {
        const savedForms: string[] = [];
        const breakForms: string[] = [];
        const partiallySavedForms: string[] = [];
        const errorForms: string[] = [];

        const determinedFormCommands: Array<ValueOf<typeof formCommands>> = [];

        const largestNotesPosition = statuses.reduce(
            (acc, status) => (status.position?.notes && status.position.notes > acc ? status.position.notes : acc),
            0,
        );
        const largestFormsPosition = statuses.reduce(
            (acc, status) => (status.position?.forms && status.position.forms > acc ? status.position.forms : acc),
            0,
        );
        // eslint-disable-next-line no-restricted-syntax
        for (const status of statuses) {
            // these are the forms that should break submit, i.e. the user changes data that would lead to a change in the applicant hash
            if (status.breakSubmit) breakForms.push(...status.breakSubmit);
            if (status.error && status.saved) partiallySavedForms.push(status.form);
            else if (status.error) errorForms.push(status.form); // maybe we should filter out the unaltered errors?
            else if (status.saved) savedForms.push(status.form);
            // these are the generated commands for the respective form
            if (status.commands) determinedFormCommands.push(...status.commands);
        }
        return {
            breakForms,
            determinedFormCommands,
            errorForms,
            largestFormsPosition: largestFormsPosition || undefined,
            largestNotesPosition: largestNotesPosition || undefined,
            partiallySavedForms,
            savedForms,
        };
    }, []);

    const trigger = useCallback(async () => {
        const submitCallbacks = Object.entries(ref.current);
        // checks if the user has a shared household and lives in his own house
        if (isLivingInSharedHousehold && (isLivingInOwnHouseApplicant1 || isLivingInOwnHouseApplicant2)) {
            // checks if there is no self occupied real estate for the shared household
            if (
                !hasSelfOccupiedRealEstateShared &&
                !hasSelfOccupiedRealEstateApplicant1 &&
                !hasSelfOccupiedRealEstateApplicant2
            ) {
                toast.error(t('REAL_ESTATE.TOASTS.ERROR_SAVING_MISSING_SHARED'));
                return false;
            }
        } else {
            // checks if there is a shared, self occupied real estate
            if (hasSelfOccupiedRealEstateShared) {
                toast.error(t('REAL_ESTATE.TOASTS.ERROR_SAVING_UNNECESSARY_SHARED'));
                return false;
            }
            // check if any of the applicants live in their own house but don't have a self occupied real estate
            if (
                (isLivingInOwnHouseApplicant1 && !hasSelfOccupiedRealEstateApplicant1) ||
                (isLivingInOwnHouseApplicant2 && !hasSelfOccupiedRealEstateApplicant2)
            ) {
                toast.error(t('REAL_ESTATE.TOASTS.ERROR_SAVING_MISSING_SELF_OCCUPATION'));
                return false;
            }
            // check if any of the applicants don't live in their own house but have a self occupied real estate
            if (
                (!isLivingInOwnHouseApplicant1 && hasSelfOccupiedRealEstateApplicant1) ||
                (!isLivingInOwnHouseApplicant2 && hasSelfOccupiedRealEstateApplicant2)
            ) {
                toast.error(t('REAL_ESTATE.TOASTS.ERROR_SAVING_UNNECESSARY_SELF_OCCUPATION'));
                return false;
            }
        }

        // validate every form first, so that no form is saved while another one is invalid
        const invalidForms: string[] = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const [form, validate] of Object.entries(validators.current)) {
            // eslint-disable-next-line no-await-in-loop
            if (!(await validate())) invalidForms.push(form);
        }
        if (invalidForms.length > 0) {
            showSubmitToast({ errorForms: invalidForms, partiallySavedForms: [], savedForms: [], t });
            return false;
        }

        const statuses: Status[] = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const [form, handleSubmit] of submitCallbacks) {
            statuses.push(
                // eslint-disable-next-line no-await-in-loop
                await new Promise((resolve) => {
                    const setSubmitStatus: SetSubmitStatus = (status) => resolve({ ...status, form });
                    handleSubmit(setSubmitStatus)();
                }),
            );
        }

        const {
            breakForms,
            determinedFormCommands,
            errorForms,
            largestFormsPosition,
            largestNotesPosition,
            partiallySavedForms,
            savedForms,
        } = handleStatuses(statuses);

        if (breakForms.includes('living')) {
            return false;
        }

        showSubmitToast({ errorForms, partiallySavedForms, savedForms, t });

        if (
            savedForms.includes(LEAD_DETAILS_FORMS.PERSONAL_DATA) &&
            (determinedFormCommands.includes(formCommands.changeApplicantName) ||
                determinedFormCommands.includes(formCommands.updateApplicantBirthData) ||
                determinedFormCommands.includes(formCommands.updateApplicantResidenceData) ||
                determinedFormCommands.includes(formCommands.changeApplicantEmail) ||
                determinedFormCommands.includes(formCommands.changeApplicantPhone))
        ) {
            const applicantIds = [lead?.antragsteller1.id, lead?.antragsteller2?.id].filter(Boolean) as string[];

            let changeType: ValueOf<typeof NOTE_CHANGE_TYPE> = NOTE_CHANGE_TYPE.applicant;

            if (largestNotesPosition && largestFormsPosition && largestNotesPosition > largestFormsPosition) {
                changeType = NOTE_CHANGE_TYPE.notes;
            }

            const queryKeyApplicantNotes = queryKeys.getApplicantNotes(applicantIds, leadId ?? '', changeType);

            setChangeType(changeType);
            if (largestNotesPosition) setQueryPosition(JSON.stringify(queryKeyApplicantNotes), largestNotesPosition);

            queryClient.invalidateQueries({
                queryKey: appendPositionToQueryKey.custom(queryKeyApplicantNotes, largestNotesPosition),
            });
        }

        const queryKey = queryKeys.get(leadId || '');
        if (largestFormsPosition) setQueryPosition(JSON.stringify(queryKey), largestFormsPosition);
        if (savedForms.length || partiallySavedForms.length) await queryClient.invalidateQueries({ queryKey });

        const successful = errorForms.length === 0 && partiallySavedForms.length === 0 && breakForms.length === 0;
        if (successful) clearDirtyState();
        return successful;
    }, [
        isLivingInSharedHousehold,
        isLivingInOwnHouseApplicant1,
        isLivingInOwnHouseApplicant2,
        handleStatuses,
        leadId,
        queryClient,
        hasSelfOccupiedRealEstateShared,
        hasSelfOccupiedRealEstateApplicant1,
        hasSelfOccupiedRealEstateApplicant2,
        t,
        lead?.antragsteller1.id,
        lead?.antragsteller2?.id,
        clearDirtyState,
    ]);

    const value = useMemo(
        () => ({
            addedRowIds,
            hasDirtyForms: dirtyFormIds.length > 0,
            hasSelfOccupiedRealEstateApplicant1,
            hasSelfOccupiedRealEstateApplicant2,
            hasSelfOccupiedRealEstateShared,
            isDirty,
            isLivingInOwnHouseApplicant1,
            isLivingInOwnHouseApplicant2,
            isLivingInSharedHousehold,
            registerAddedRow,
            registerDirtyState,
            registerReset,
            registerSubmit,
            reset,
            resetAll,
            setHasSelfOccupiedRealEstateApplicant1,
            setHasSelfOccupiedRealEstateApplicant2,
            setHasSelfOccupiedRealEstateShared,
            setIsLivingInOwnHouseApplicant1,
            setIsLivingInOwnHouseApplicant2,
            setIsLivingInSharedHousehold,
            trigger,
        }),
        [
            addedRowIds,
            dirtyFormIds,
            hasSelfOccupiedRealEstateApplicant1,
            hasSelfOccupiedRealEstateApplicant2,
            hasSelfOccupiedRealEstateShared,
            isDirty,
            isLivingInOwnHouseApplicant1,
            isLivingInOwnHouseApplicant2,
            isLivingInSharedHousehold,
            registerAddedRow,
            registerDirtyState,
            registerReset,
            registerSubmit,
            reset,
            resetAll,
            setHasSelfOccupiedRealEstateApplicant1,
            setHasSelfOccupiedRealEstateApplicant2,
            setHasSelfOccupiedRealEstateShared,
            setIsLivingInOwnHouseApplicant1,
            setIsLivingInOwnHouseApplicant2,
            setIsLivingInSharedHousehold,
            trigger,
        ],
    );

    return <ContextProvider value={value}>{children}</ContextProvider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export { useLeadDetailsFormContext };
export default LeadDetailsFormContextProvider;
